if (!customElements.get('country-selector')) {
  class CountrySelector extends HTMLElement {
    constructor() {
      super();
      this.provinceElement = this.querySelector('.js-shipping-province');
      this.countryElement = this.querySelector('.js-shipping-country');
      this.provinceWrapper = this.querySelector('.js-province-container');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      CountrySelector.selectOption(this.countryElement, this.countryElement.dataset.default);

      this.updateProvinceOptions();

      if (this.provinceElement.dataset.default && this.provinceElement.options.length > 0) {
        CountrySelector.selectOption(this.provinceElement, this.provinceElement.dataset.default);
      }

      this.countryElement.addEventListener('change', this.updateProvinceOptions.bind(this));
    }

    /**
     * Updates the province/state options based on selected country.
     */
    updateProvinceOptions() {
      const selectedCountry = this.countryElement.options[this.countryElement.selectedIndex];
      const provinceList = JSON.parse(selectedCountry.dataset.provinces);

      // Clear existing options in the province field.
      Array.from(this.provinceElement.options).forEach((option) => option.remove());

      if (provinceList && provinceList.length === 0) {
        this.provinceWrapper.hidden = true;
      } else {
        provinceList.forEach((province) => {
          const provinceOption = document.createElement('option');
          [provinceOption.value, provinceOption.innerHTML] = province;
          this.provinceElement.appendChild(provinceOption);
        });

        this.provinceWrapper.hidden = false;
      }
    }

    /**
     * Sets the selected option of a <select> element.
     * @param {Element} dropdown - Country or province <select> element.
     * @param {string} value - The value to be selected in the dropdown.
     */
    static selectOption(dropdown, value) {
      Array.from(dropdown.options).forEach((option, index) => {
        if (option.value === value || option.innerHTML === value) {
          dropdown.selectedIndex = index;
        }
      });
    }
  }

  customElements.define('country-selector', CountrySelector);
}

if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.submitButton = this.querySelector('button');
      this.shippingForm = this.querySelector('form');
      this.shippingForm.addEventListener('submit', this.processFormSubmit.bind(this));
      this.shippingForm.addEventListener('input', this.invalidateResult.bind(this));
      this.shippingForm.addEventListener('change', this.invalidateResult.bind(this));
    }

    initialize() {
      this.countryField = this.querySelector('.js-shipping-country');
      this.provinceField = this.querySelector('.js-shipping-province');
      this.cityField = this.querySelector('.js-city-input');
      this.addressField = this.querySelector('.js-address-input');
      this.zipField = this.querySelector('.js-zip-input');
      this.ratesDisplay = this.querySelector('.js-shipping-rate');
      this.errorDisplay = this.querySelector('.js-shipping-error');
      this.networkErrorDisplay = this.querySelector('.js-shipping-network-error');
      this.isInitialized = true;
    }

    clearResult() {
      this.errorDisplay.hidden = true;
      this.networkErrorDisplay.hidden = true;
      this.ratesDisplay.hidden = true;
    }

    setLoading(loading) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-disabled', loading);
      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (loading) {
        this.submitButton.classList.add('loading');
        spinner.classList.remove('hidden', 'spinning-complete');
      } else {
        this.submitButton.classList.remove('loading');
        spinner.classList.add('hidden', 'spinning-complete');
      }
    }

    invalidateResult() {
      if (!this.isInitialized) this.initialize();
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.requestController?.abort();
      this.requestController = null;
      this.clearResult();
      this.setLoading(false);
    }

    disconnectedCallback() {
      this.invalidateResult();
    }

    /**
     * Handles the form submission for calculating shipping rates.
     * @param {object} event - The submit event object.
     */
    async processFormSubmit(event) {
      event.preventDefault();
      if (!this.isInitialized) this.initialize();

      this.invalidateResult();
      const requestVersion = this.requestVersion;
      const controller = new AbortController();
      this.requestController = controller;
      this.setLoading(true);

      const queryParams = new URLSearchParams();
      for (const [name, field] of [
        ['zip', this.zipField], ['country', this.countryField], ['province', this.provinceField],
        ['city', this.cityField], ['address1', this.addressField],
      ]) {
        if (field) queryParams.set(`shipping_address[${name}]`, field.value);
      }

      try {
        const response = await fetch(`${window.routes.cart_url}/shipping_rates.json?${queryParams}`, { signal: controller.signal });
        const resultData = await response.json();
        if (requestVersion !== this.requestVersion) return;

        if (response.ok) {
          this.displayRates(resultData);
        } else {
          this.displayErrors(resultData);
        }
      } catch (error) {
        if (requestVersion === this.requestVersion && error.name !== 'AbortError') {
          this.networkErrorDisplay.hidden = false;
        }
      } finally {
        if (requestVersion === this.requestVersion) {
          this.requestController = null;
          this.setLoading(false);
        }
      }
    }

    /**
     * Displays the available shipping rates.
     * @param {object} data - The response data from the shipping API.
     */
    displayRates(data) {
      const headingElement = this.ratesDisplay.querySelector('.js-shipping-rate-heading');
      const textElement = this.ratesDisplay.querySelector('.js-shipping-rate-text');
      textElement.innerHTML = '';

      if (data.shipping_rates && data.shipping_rates.length) {
        const headingText = data.shipping_rates.length === 1 ? 'singleRate' : 'multipleRates';
        const rateOptions = document.createElement('ul');
        rateOptions.className = 'mt-1 t5';
        const priceFormatter = new Intl.NumberFormat(document.documentElement.lang || 'pt-PT', {
          style: 'currency',
          currency: this.dataset.currency,
        });

        data.shipping_rates.forEach((rate) => {
          const rateOption = document.createElement('li');
          rateOption.textContent = `${rate.name}: ${priceFormatter.format(Number(rate.price))}`;
          rateOptions.appendChild(rateOption);
        });

        headingElement.textContent = window.strings.cart.shippingCalculator[headingText];
        textElement.appendChild(rateOptions);
        headingElement.hidden = false;
      } else {
        headingElement.hidden = true;
        textElement.innerHTML = window.strings.cart.shippingCalculator.noRates;
      }

      this.ratesDisplay.hidden = false;
    }

    /**
     * Displays errors related to shipping rate retrieval.
     * @param {object} data - The error data from the shipping API.
     */
    displayErrors(data) {
      let errorMessages = '';

      Object.keys(data).forEach((key) => {
        errorMessages += `<li>${data[key]}</li>`;
      });

      this.errorDisplay.querySelector('.js-shipping-error-content').innerHTML = `<ul class="mt-1 t5">${errorMessages}</ul>`;
      this.errorDisplay.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
